import * as THREE from 'three';
import { bboxOfPoly, pointInPoly } from '../../shared/geom.js';
import { Heightfield } from '../../shared/heightfield.js';
import type { World } from '../../shared/types.js';

/**
 * How much of the sky each part of the street can actually see, baked once per world.
 *
 * The sky is the light that fills shadows in, and a lane three metres wide between five-storey
 * tenements sees a strip of it overhead and nothing else, while a market square sees all of
 * it. Lighting both with the same hemisphere is the single biggest reason a procedural city
 * looks like a model of one: every shadowed surface is the same soft grey, wherever it is. A
 * real-time answer — screen-space ambient occlusion — is a pass over every pixel every
 * frame, and it only knows what is on screen. But the city does not move, so the answer
 * does not change, and it can be worked out once from the footprints.
 *
 * For every few metres of open ground this looks out in ten directions for the steepest
 * building edge within `REACH`, and turns that into the share of an overcast sky a flat
 * surface there receives: a horizon at elevation θ in one direction leaves cos²θ of that
 * direction's sky, averaged round the compass. Ten rays, because eight leave a visible
 * star of light down the diagonals of a gridded block.
 *
 * Three things are stored per texel: that visibility; the height of the buildings that took
 * the sky away, so a wall can be told how far up it has to go before it is out of the street
 * and into the open; and the ground height, so a wall knows where its own foot is on a hill.
 * The world's materials read it back at their own position and scale only their *indirect*
 * light by it. The sun is untouched — it either reaches a surface or the shadow map says it
 * does not — which is what lets a shaft of sunlight down a dark lane look like one.
 */

/**
 * Metres per texel at best; coarser for a large region so the field stays within 400². A
 * street is two to four texels across at that, which is plenty for light this soft — and
 * the work goes with the square of it, on the main thread, while the world is being built.
 */
const MIN_CELL = 2.5;
const MAX_TEXELS = 400;
/**
 * How far out a building can still take sky away, metres. A thirty-metre block sixty metres
 * off leaves four fifths of that direction's sky, so this is about where it stops mattering.
 */
const REACH = 60;
const DIRECTIONS = 10;
/** The eye height the horizon is measured from: about a car's roof. */
const RECEIVER = 1.2;

export interface SkyFieldData {
  width: number;
  depth: number;
  /** World X and Z of texel (0, 0)'s outer corner, and metres per texel. */
  minX: number;
  minZ: number;
  cell: number;
  /** Per texel: visibility 0–1, occluder height in metres, ground height in metres. */
  visibility: Float32Array;
  canyon: Float32Array;
  ground: Float32Array;
}

/** Pure, so the tests can ask what a lane sees without a GL context. */
export function computeSkyField(world: World): SkyFieldData {
  const terrain = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();
  const margin = 40;
  const minX = -world.extent.x - margin;
  const minZ = -world.extent.z - margin;
  const spanX = (world.extent.x + margin) * 2;
  const spanZ = (world.extent.z + margin) * 2;
  const cell = Math.max(MIN_CELL, Math.max(spanX, spanZ) / MAX_TEXELS);
  const width = Math.ceil(spanX / cell);
  const depth = Math.ceil(spanZ / cell);
  const count = width * depth;

  // Building tops above the ground, rasterised at texel centres. A courtyard is open sky.
  const heights = new Float32Array(count);
  for (const b of world.buildings) {
    if (b.height <= 0) continue;
    const bb = bboxOfPoly(b.poly);
    const i0 = Math.max(0, Math.floor((bb.minX - minX) / cell));
    const i1 = Math.min(width - 1, Math.floor((bb.maxX - minX) / cell));
    const j0 = Math.max(0, Math.floor((bb.minZ - minZ) / cell));
    const j1 = Math.min(depth - 1, Math.floor((bb.maxZ - minZ) / cell));
    for (let j = j0; j <= j1; j++) {
      const z = minZ + (j + 0.5) * cell;
      for (let i = i0; i <= i1; i++) {
        const x = minX + (i + 0.5) * cell;
        if (!pointInPoly(b.poly, x, z)) continue;
        if (b.holes.some((hole) => pointInPoly(hole, x, z))) continue;
        const k = j * width + i;
        if (b.height > heights[k]) heights[k] = b.height;
      }
    }
  }

  const dirX: number[] = [];
  const dirZ: number[] = [];
  for (let d = 0; d < DIRECTIONS; d++) {
    const a = (d / DIRECTIONS) * Math.PI * 2;
    dirX.push(Math.cos(a));
    dirZ.push(Math.sin(a));
  }
  const steps = Math.ceil(REACH / cell);
  let tallestAnywhere = 0;
  for (let k = 0; k < count; k++) if (heights[k] > tallestAnywhere) tallestAnywhere = heights[k];
  const reachAbove = tallestAnywhere - RECEIVER;

  const visibility = new Float32Array(count);
  const canyon = new Float32Array(count);
  const ground = new Float32Array(count);
  for (let j = 0; j < depth; j++) {
    for (let i = 0; i < width; i++) {
      const k = j * width + i;
      ground[k] = terrain.isFlat ? 0 : terrain.heightAt(minX + (i + 0.5) * cell, minZ + (j + 0.5) * cell);
      // Inside a footprint nothing samples the ground, but a wall's foot sometimes rounds
      // into its own building's texel; reading it as open sky there is the safe mistake.
      if (heights[k] > 0) {
        visibility[k] = 1;
        canyon[k] = heights[k];
        continue;
      }
      let sky = 0;
      let occluder = 0;
      for (let d = 0; d < DIRECTIONS; d++) {
        // The steepest building edge seen along this direction, as rise over run.
        let steepest = 0;
        let tallest = 0;
        // A texel at a time close in, two at a time beyond eight: a building is never as thin
        // as two texels, and the far half of a ray is where an open square spends its time.
        for (let s = 1; s <= steps; s += s < 8 ? 1 : 2) {
          // Nothing further out can be steeper than the tallest building in the city would
          // be at this distance — and once that is below what has already been found, the
          // rest of the ray is wasted. Most rays in a street stop within a few texels.
          if (reachAbove <= steepest * s * cell) break;
          const si = Math.round(i + dirX[d] * s);
          const sj = Math.round(j + dirZ[d] * s);
          if (si < 0 || sj < 0 || si >= width || sj >= depth) break;
          const h = heights[sj * width + si];
          if (h <= RECEIVER) continue;
          const slope = (h - RECEIVER) / (s * cell);
          if (slope > steepest) {
            steepest = slope;
            tallest = h;
          }
        }
        sky += 1 / (1 + steepest * steepest);
        occluder += tallest;
      }
      visibility[k] = sky / DIRECTIONS;
      canyon[k] = occluder / DIRECTIONS;
    }
  }

  return { width, depth, minX, minZ, cell, visibility: blur(visibility, width, depth), canyon, ground };
}

/** One 3×3 box pass: the rays are a texel apart, and the edges of a pool of sky should not be. */
function blur(src: Float32Array, width: number, depth: number): Float32Array {
  const out = new Float32Array(src.length);
  for (let j = 0; j < depth; j++) {
    for (let i = 0; i < width; i++) {
      let sum = 0;
      let n = 0;
      for (let dj = -1; dj <= 1; dj++) {
        const jj = j + dj;
        if (jj < 0 || jj >= depth) continue;
        for (let di = -1; di <= 1; di++) {
          const ii = i + di;
          if (ii < 0 || ii >= width) continue;
          sum += src[jj * width + ii];
          n++;
        }
      }
      out[j * width + i] = sum / n;
    }
  }
  return out;
}

export interface SkyField {
  /**
   * Scales a world material's indirect light by what its position can see of the sky.
   * `wall` materials read the field a little way out from their face, at the street, and
   * come back up to open sky with height. Chains onto any `onBeforeCompile` already set.
   */
  patch(material: THREE.Material, kind: 'ground' | 'wall'): void;
  dispose(): void;
}

/**
 * The darkest the indirect light gets, at the bottom of the narrowest lane. Not zero:
 * nothing here models the light the walls bounce into each other, and a lane is never black.
 */
const FLOOR = 0.3;

export function buildSkyField(world: World): SkyField {
  const field = computeSkyField(world);
  const { width, depth, cell } = field;
  const data = new Uint16Array(width * depth * 4);
  for (let k = 0; k < width * depth; k++) {
    data[k * 4] = THREE.DataUtils.toHalfFloat(field.visibility[k]);
    data[k * 4 + 1] = THREE.DataUtils.toHalfFloat(field.canyon[k]);
    data[k * 4 + 2] = THREE.DataUtils.toHalfFloat(field.ground[k]);
    data[k * 4 + 3] = THREE.DataUtils.toHalfFloat(1);
  }
  const texture = new THREE.DataTexture(data, width, depth, THREE.RGBAFormat, THREE.HalfFloatType);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;

  const uniforms = {
    skyField: { value: texture },
    skyRect: {
      value: new THREE.Vector4(field.minX, field.minZ, 1 / (width * cell), 1 / (depth * cell)),
    },
  };

  return {
    patch(material, kind) {
      const previous = material.onBeforeCompile.bind(material);
      const previousKey = material.customProgramCacheKey.bind(material);
      const wall = kind === 'wall';
      material.onBeforeCompile = (shader, renderer) => {
        previous(shader, renderer);
        Object.assign(shader.uniforms, uniforms);
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec3 vSkyPos;')
          .replace(
            '#include <project_vertex>',
            '#include <project_vertex>\n\tvSkyPos = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;',
          );
        // After the lights are summed, so it reaches the sky's light and nothing else; and
        // before `aomap_fragment`, where the street lights are added, so it does not dim them.
        const sample = wall
          ? // Two metres out from the face, where the street is. `normal` is view space.
            '\tvec3 skyOut = inverseTransformDirection( normal, viewMatrix );\n' +
            '\tvec2 skyAt = vSkyPos.xz + normalize( skyOut.xz + 1e-5 ) * 2.0;\n' +
            '\tvec4 skyS = texture2D( skyField, ( skyAt - skyRect.xy ) * skyRect.zw );\n' +
            // Up the wall the sky opens out, fully by the height of what was in the way.
            '\tfloat skyUp = smoothstep( 0.0, max( skyS.g, 4.0 ), vSkyPos.y - skyS.b );\n' +
            `\tfloat skySeen = mix( max( skyS.r, ${FLOOR.toFixed(2)} ), 1.0, skyUp );\n`
          : '\tvec4 skyS = texture2D( skyField, ( vSkyPos.xz - skyRect.xy ) * skyRect.zw );\n' +
            `\tfloat skySeen = max( skyS.r, ${FLOOR.toFixed(2)} );\n`;
        shader.fragmentShader = shader.fragmentShader
          .replace(
            '#include <common>',
            '#include <common>\nuniform sampler2D skyField;\nuniform vec4 skyRect;\nvarying vec3 vSkyPos;',
          )
          .replace(
            '#include <lights_fragment_end>',
            '#include <lights_fragment_end>\n' +
              sample +
              '\treflectedLight.indirectDiffuse *= skySeen;\n' +
              '\treflectedLight.indirectSpecular *= skySeen;',
          );
      };
      material.customProgramCacheKey = () => `${previousKey()}|sky:${kind}`;
      material.needsUpdate = true;
    },
    dispose() {
      texture.dispose();
    },
  };
}
